import { Notification } from "@/utils/notificationUtils";
import { openBlobFile } from "@/utils/utils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { productApi } from "../../../modules/product/api";
import {
  CreateUpdateProduct,
  CreateUpdateProductGroup,
  ProductFilterExportRequest,
  ProductFilterPageRequest,
} from "../../../modules/product/types";
import type { UUID } from "../../../typings/global";
import { EntityObject, SearchPageRequest } from "../../types";
import { invalidateEnumeration } from "./enumerations";

type CreateUpdateProps = {
  invalidateFilter?: boolean;
};

export const PRODUCT_QUERY_KEYS = {
  filterProductGroups: (filter?: SearchPageRequest) =>
    filter ? ["filterProductGroups", filter] : ["filterProductGroups"],
  createProductGroup: () => ["createProductGroup"],
  updateProductGroup: () => ["updateProductGroup"],
  deleteProductGroup: () => ["deleteProductGroup"],
  filterProducts: (filter?: ProductFilterPageRequest) => (filter ? ["filterProducts", filter] : ["filterProducts"]),
  downloadProductsExport: () => ["downloadProductsExport"],
  createProduct: () => ["createProduct"],
  updateProduct: () => ["updateProduct"],
  deleteProduct: () => ["deleteProduct"],
};

export const useFilterProductGroups = (filter: SearchPageRequest, isEnabled?: boolean) => {
  return useQuery({
    queryKey: PRODUCT_QUERY_KEYS.filterProductGroups(filter),
    queryFn: async () => {
      const { data } = await productApi.filterProductGroups(filter);

      return data;
    },
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  });
};

export const useCreateProductGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.createProductGroup(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateProductGroup) => {
      const { data } = await productApi.createProductGroup(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCT_GROUPS);
      queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProductGroups() });
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateProductGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.updateProductGroup(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateProductGroup>) => {
      const { data } = await productApi.updateProductGroup(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCT_GROUPS);
      queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProductGroups() });
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteProductGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.deleteProductGroup(),
    mutationFn: (id: UUID) => {
      return productApi.deleteProductGroup({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCT_GROUPS);
      queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProductGroups() });
      Notification.itemDeletedNotification();
    },
  });
};

export const useFilterProducts = (filter: ProductFilterPageRequest, isEnabled?: boolean) => {
  return useQuery({
    queryKey: PRODUCT_QUERY_KEYS.filterProducts(filter),
    queryFn: async () => {
      const { data } = await productApi.filterProducts(filter);

      return data;
    },
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  });
};

export const useDownloadProductsExport = () => {
  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.downloadProductsExport(),
    mutationFn: async (payload: ProductFilterExportRequest) => {
      const response = await productApi.downloadProductsExport(payload);
      openBlobFile(response, true);
    },
  });
};

export const useCreateProduct = ({ invalidateFilter }: CreateUpdateProps = { invalidateFilter: true }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.createProduct(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateProduct) => {
      const { data } = await productApi.createProduct(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCTS);
      if (invalidateFilter) {
        queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProducts() });
      }
      Notification.itemCreatedNotification();
    },
  });
};

export const useUpdateProduct = ({ invalidateFilter }: CreateUpdateProps = { invalidateFilter: true }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.updateProduct(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateProduct>) => {
      const { data } = await productApi.updateProduct(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCTS);
      if (invalidateFilter) {
        queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProducts() });
      }
      Notification.itemUpdatedNotification();
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: PRODUCT_QUERY_KEYS.deleteProduct(),
    mutationFn: (id: UUID) => {
      return productApi.deleteProduct({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.PRODUCTS);
      queryClient.invalidateQueries({ queryKey: PRODUCT_QUERY_KEYS.filterProducts() });
      Notification.itemDeletedNotification();
    },
  });
};
